/**
 * i18n setup dla redesignu Terroir.
 *
 * Zasada: wszystkie stringi UI z `t('klucz')`. Hardcoded teksty po polsku
 * tylko w danych domenowych (nazwy roślin z bazy, notatki użytkownika,
 * wpisy do dziennika - to nie jest UI, to content).
 *
 * Default język: PL. Auto-detect na podstawie navigator.language.
 * Override: localStorage `i18nextLng` (zapamiętane między sesjami).
 *
 * Użycie w komponencie:
 *   import { useTranslation } from 'react-i18next';
 *   const { t, i18n } = useTranslation();
 *   <h1>{t('today.title')}</h1>
 *   <button onClick={() => i18n.changeLanguage('en')}>EN</button>
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import pl from './locales/pl.json';
import en from './locales/en.json';

// PL jest językiem domyślnym i wymuszonym — większość hardcoded stringów wciąż
// po polsku (mieszanka EN/PL na pages/Plants.jsx jeśli detector wybierze EN-US).
// Dopóki nie dokończymy pełnego EN namespace + nie sprawdzimy każdego ekranu —
// trzymamy wymuszone PL. Switcher języka (LanguageSwitcher.jsx) dalej działa
// po świadomej zmianie usera (zapisuje do localStorage 'i18nextLng').
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: 'pl',
    fallbackLng: 'pl',
    supportedLngs: ['pl', 'en'],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Tylko localStorage — user musi sam zmienić, nie wykrywamy z przeglądarki
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    resources: {
      pl: { translation: pl },
      en: { translation: en },
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;

/**
 * Helper: format daty zgodnie z aktualnym językiem (zamiast custom formatDate).
 * Używa Intl.DateTimeFormat - obsługuje obie wersje natywnie.
 */
export function formatLocalDate(dateStr, style = 'short') {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const lng = i18n.language || 'pl';

  if (style === 'iso') return d.toISOString().slice(0, 10);

  const presets = {
    short:    { day: 'numeric', month: 'short' },         // 23 kwi / Apr 23
    long:     { day: 'numeric', month: 'long', year: 'numeric' },
    numeric:  { day: '2-digit', month: '2-digit', year: 'numeric' },
    weekday:  { weekday: 'short', day: 'numeric', month: 'numeric' },
  };

  return new Intl.DateTimeFormat(lng, presets[style] || presets.short).format(d);
}

/**
 * Helper: format relatywny ("Dziś", "Jutro", "Za 3 dni").
 */
export function formatRelativeDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

  const lng = i18n.language || 'pl';
  if (diffDays === 0) return i18n.t('common.today');
  if (diffDays === 1) return i18n.t('common.tomorrow');
  if (diffDays === -1) return i18n.t('common.yesterday');

  // Intl.RelativeTimeFormat dla większych przedziałów
  const rtf = new Intl.RelativeTimeFormat(lng, { numeric: 'auto' });
  if (diffDays >= -7 && diffDays <= 30) return rtf.format(diffDays, 'day');
  return formatLocalDate(dateStr, 'short');
}
